import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import LetterWheel, {
    buildWheelTiles,
    lettersForWheel,
} from "../../prototype/mobile-word-wheel/components/LetterWheel";
import {
    buildDisplayGrid,
    buildWordClueNumbers,
    formatCellWordNumberLabel,
    normalizeWord,
    parseWordPositions,
    puzzleCellKeys,
    resolveWordWheelGridSize,
    usedPuzzleBounds,
} from "../../prototype/mobile-word-wheel/lib/gridReveal";
import { GRID_CREAM } from "../../prototype/mobile-word-wheel/lib/gridTheme";
import { resolveScreenshotPlayColors } from "../../prototype/mobile-word-wheel/lib/theme";
import ApiService from "../../services/ApiService";
import { WEB_AD_WORD_WHEEL_TPL_ID } from "./webAdShots";

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse
        .split(/[\n,;]+/)
        .map((w) => w.trim())
        .filter(Boolean)
        .map(normalizeWord);
}

/** Prefer a short unfound word that the wheel can spell (for the static drag line). */
function pickDemoDragWord(targetWords, foundSet, tiles) {
    const unfound = targetWords.filter((w) => w && !foundSet.has(w));
    const tryWord = (word) => {
        const letters = word.split("");
        const used = new Set();
        const indices = [];
        for (const ch of letters) {
            let found = -1;
            for (let i = 0; i < tiles.length; i += 1) {
                if (used.has(i)) continue;
                if ((tiles[i].letter || "").toUpperCase() === ch) {
                    found = i;
                    break;
                }
            }
            if (found < 0) return null;
            used.add(found);
            indices.push(found);
        }
        return indices;
    };
    for (const len of [4, 3, 5]) {
        for (const word of unfound.filter((w) => w.length === len)) {
            const indices = tryWord(word);
            if (indices) return { word, indices };
        }
    }
    return null;
}

/**
 * Live word-wheel grid + letter wheel for web Google Ads.
 * Template: {@link WEB_AD_WORD_WHEEL_TPL_ID}.
 */
export default function WebAdWordWheelBoard({
    boardSize = 280,
    wheelSize = 250,
    /** `side` = grid | wheel (landscape). `stack` = wheel above grid (square). */
    layout = "side",
}) {
    const [puzzle, setPuzzle] = useState(null);
    const [failed, setFailed] = useState(false);
    const playColors = useMemo(() => resolveScreenshotPlayColors("random"), []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await ApiService.getData(
                    `/v1/puzzle/wordwheel-tpl/${encodeURIComponent(WEB_AD_WORD_WHEEL_TPL_ID)}`,
                );
                if (cancelled) return;
                if (!data) {
                    setFailed(true);
                    return;
                }
                setPuzzle(data);
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const targetWords = useMemo(() => parseWords(puzzle?.wordsInUse), [puzzle]);
    const foundWords = useMemo(() => {
        // Mid-play look: reveal roughly half the words.
        const n = Math.max(1, Math.floor(targetWords.length / 2));
        return targetWords.slice(0, n);
    }, [targetWords]);
    const foundSet = useMemo(() => new Set(foundWords), [foundWords]);

    const baseWheelLetters = useMemo(
        () => lettersForWheel(targetWords, puzzle?.lettersGrid, puzzle?.details),
        [targetWords, puzzle],
    );
    const wheelTiles = useMemo(
        () => buildWheelTiles(baseWheelLetters, puzzle?.id || WEB_AD_WORD_WHEEL_TPL_ID),
        [baseWheelLetters, puzzle?.id],
    );
    const wordPositions = useMemo(
        () => parseWordPositions(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates],
    );
    const puzzleCells = useMemo(() => puzzleCellKeys(wordPositions), [wordPositions]);
    const gridSize = useMemo(() => resolveWordWheelGridSize(puzzle), [puzzle]);
    const displayGrid = useMemo(
        () => buildDisplayGrid(foundWords, wordPositions, new Map(), gridSize),
        [foundWords, wordPositions, gridSize],
    );
    const { cellToNumbers } = useMemo(
        () => buildWordClueNumbers(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates],
    );
    const gridBounds = useMemo(
        () => usedPuzzleBounds(puzzleCells, gridSize),
        [puzzleCells, gridSize],
    );
    const visibleRows = Math.max(1, gridBounds.maxRow - gridBounds.minRow + 1);
    const visibleCols = Math.max(1, gridBounds.maxCol - gridBounds.minCol + 1);
    const cellPx = Math.floor(boardSize / Math.max(visibleRows, visibleCols, 1));
    const gridW = cellPx * visibleCols;
    const gridH = cellPx * visibleRows;

    const drag = useMemo(
        () => pickDemoDragWord(targetWords, foundSet, wheelTiles),
        [targetWords, foundSet, wheelTiles],
    );
    const selectedIndices = drag?.indices ?? [];
    const selectedWordCells = useMemo(() => {
        const word = drag?.word;
        if (!word || !wordPositions[word]) return new Set();
        return new Set(wordPositions[word].map((p) => `${p.row},${p.col}`));
    }, [drag, wordPositions]);

    const wheelColors = useMemo(
        () => ({
            ...playColors,
            surface: playColors.letterBg,
            border: playColors.letterBorder,
            textOnSurface: playColors.letterText,
        }),
        [playColors],
    );

    if (failed) {
        return (
            <Box
                sx={{
                    width: boardSize,
                    height: boardSize,
                    borderRadius: 2,
                    bgcolor: "#E2E8F0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 600,
                    textAlign: "center",
                    px: 2,
                }}
            >
                Could not load word wheel
            </Box>
        );
    }

    if (!puzzle) {
        return (
            <Box
                sx={{
                    width: boardSize,
                    height: boardSize,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress size={36} sx={{ color: "#3BA666" }} />
            </Box>
        );
    }

    const grid = (
        <Box
            data-web-ad-word-wheel="1"
            sx={{
                width: gridW,
                height: gridH,
                display: "grid",
                gridTemplateColumns: `repeat(${visibleCols}, ${cellPx}px)`,
                gridTemplateRows: `repeat(${visibleRows}, ${cellPx}px)`,
                gap: "2px",
                flexShrink: 0,
                boxShadow: "0 14px 32px rgba(15,23,42,0.16)",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "rgba(15,23,42,0.06)",
                p: "2px",
            }}
        >
            {Array.from({ length: visibleRows }, (_, rowOffset) =>
                Array.from({ length: visibleCols }, (_, colOffset) => {
                    const row = gridBounds.minRow + rowOffset;
                    const col = gridBounds.minCol + colOffset;
                    const letter = displayGrid[row]?.[col] || "";
                    const cellKey = `${row},${col}`;
                    const isPuzzleCell = puzzleCells.has(cellKey);
                    const isRevealed = Boolean(letter);
                    const isSelected = selectedWordCells.has(cellKey);
                    const wordNumberLabel = formatCellWordNumberLabel(cellToNumbers.get(cellKey));
                    if (!isPuzzleCell) {
                        return <Box key={cellKey} sx={{ width: cellPx, height: cellPx }} />;
                    }
                    return (
                        <Box
                            key={cellKey}
                            sx={{
                                width: cellPx,
                                height: cellPx,
                                position: "relative",
                                boxSizing: "border-box",
                                bgcolor: isSelected
                                    ? GRID_CREAM.selectedBg
                                    : isRevealed
                                      ? GRID_CREAM.revealedBg
                                      : GRID_CREAM.cellBg,
                                border: `${isSelected ? GRID_CREAM.selectedBorderWidth : GRID_CREAM.cellBorderWidth}px solid ${
                                    isSelected ? GRID_CREAM.selectedBorder : GRID_CREAM.cellBorder
                                }`,
                                borderRadius: 0.75,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontFamily:
                                    '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif',
                                fontWeight: 800,
                                fontSize: Math.round(cellPx * 0.42),
                                color: isSelected ? GRID_CREAM.selectedText : GRID_CREAM.cellText,
                            }}
                        >
                            {wordNumberLabel ? (
                                <Box
                                    component="span"
                                    sx={{
                                        position: "absolute",
                                        top: 1,
                                        left: 3,
                                        fontSize: Math.max(8, Math.round(cellPx * 0.18)),
                                        fontWeight: 700,
                                        color: GRID_CREAM.badgeText,
                                        lineHeight: 1,
                                    }}
                                >
                                    {wordNumberLabel}
                                </Box>
                            ) : null}
                            {letter}
                        </Box>
                    );
                }),
            )}
        </Box>
    );

    const wheel = (
        <Box
            sx={{
                width: wheelSize,
                height: wheelSize,
                flexShrink: 0,
                filter: "drop-shadow(0 16px 36px rgba(59,166,102,0.28))",
            }}
        >
            <LetterWheel
                tiles={wheelTiles}
                selectedIndices={selectedIndices}
                fill
                colors={wheelColors}
                backdrop="helm"
            />
        </Box>
    );

    if (layout === "stack") {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2.5 }}>
                {wheel}
                {grid}
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            {grid}
            {wheel}
        </Box>
    );
}
